// eslint-disable-next-line import/no-named-as-default
import React, { useEffect, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";

import { RootState } from "store/rootReducer";
import { useMapStore } from "stores/map";
import {
  selectHighlightContributingWellsForFacility,
  selectNetworkDepthLegend,
  selectSelectedFacilities,
  selectSelectedWells,
  selectShowNetworkOptions,
  selectWellNetworkMapRequest
} from "stores/map/mapSelectors";

import { ToolbarButton } from "components/base";
import Icon_MidstreamAllFacilities from "components/icons/Icon_MidstreamAllFacilities";
import Icon_MidstreamContributingWells from "components/icons/Icon_MidstreamContributingWells";
import Icon_MidstreamHighlight from "components/icons/Icon_MidstreamHighlight";
import Icon_MidstreamNetwork from "components/icons/Icon_MidstreamNetwork";
import Icon_MidstreamSalesFacilities from "components/icons/Icon_MidstreamSalesFacilities";
import { useMapContext } from "components/map/hooks/useMapContext";

import { EntityKind } from "../../../../models/entityKind";
import { setIsMidstreamNavigatorFilter as setIsMidstreamNavigatorFacilityFilter } from "../../../../store/features/filter/facilityFilterSlice";
import { setIsMidstreamNavigatorFilter } from "../../../../store/features/filter/filterSlice";
import {
  midstreamNavigatorActions,
  useMidstreamNavigatorContext
} from "../../../midstream/midstream-navigator/context/MidstreamNavigatorContext";
import { useClearWellToFacilityNetwork } from "../../hooks/useClearWellToFacilityNetwork";
import { useShowWellToFacilityNetwork } from "../../hooks/useShowWellToFacilityNetwork";
import { useContributingWellsFromAllFacilitiesFilter } from "./hooks/useContributingWellsFromAllFacilitiesFilter";
import { useFacilityAndWellsFilter } from "./hooks/useFacilityAndWellsFilter";
import { useFilterToSalesFacilities } from "./hooks/useFilterToSalesFacilities";
import { useFilterUndo } from "./hooks/useFilterUndo";
import { useNetworkBoundsManager } from "./hooks/useNetworkBoundsManager";

export enum FilterKey {
  HighlightWells = "highlight-wells",
  FacilityAndWells = "facility-and-wells",
  ContributingWellsFromAllFacilities = "contributing-wells-from-all-facilities",
  Network = "network", // currently not implemented and will be used in the future when we fix the logic for this
  SalesFacilities = "sales-facilities",
  WellNetwork = "well-network"
}

const FACILITY_BUTTONS = [
  FilterKey.HighlightWells,
  FilterKey.FacilityAndWells,
  FilterKey.ContributingWellsFromAllFacilities
] as const;

const WELL_BUTTONS = [FilterKey.WellNetwork, FilterKey.SalesFacilities] as const;

interface FilterButtonProps {
  filterKey: FilterKey;
}

const FilterButton: React.FC<FilterButtonProps> = ({ filterKey }) => {
  const [state, dispatch] = useMidstreamNavigatorContext();
  const { mapbox } = useMapContext();
  const reduxDispatch = useDispatch();

  // Add refs to track user intent and prevent automatic cleanup
  const userActionInProgress = useRef(false);
  const lastUserAction = useRef<"activate" | "deactivate" | null>(null);
  const networkUserDeactivated = useRef(false);

  const selectedWells = useMapStore(selectSelectedWells);
  const selectedFacilities = useMapStore(selectSelectedFacilities);

  // Track which entity type was selected last
  const [lastSelectedEntityType, setLastSelectedEntityType] =
    useState<Maybe<EntityKind>>(null);

  const { filterToFacilityAndContributingWells } = useFacilityAndWellsFilter();
  const { filterToContributingWellsFromAllFacilities } =
    useContributingWellsFromAllFacilitiesFilter();
  const { undoFilters } = useFilterUndo();
  const { filterToSalesFacilities } = useFilterToSalesFacilities(mapbox);

  const { clearEnvelopes } = useNetworkBoundsManager(mapbox);

  // Zustand map store: reads
  const wellNetworkMapRequest = useMapStore(selectWellNetworkMapRequest);

  // Zustand map store: actions
  const setWellNetworkMapRequestAction = useMapStore((s) => s.setWellNetworkMapRequest);
  const setHighlightContributingWellsForFacility = useMapStore(
    (s) => s.setHighlightContributingWellsForFacility
  );
  const clearHighlightContributingWellsForFacility = useMapStore(
    (s) => s.clearHighlightContributingWellsForFacility
  );
  const setWellNetworkFilterActivated = useMapStore(
    (s) => s.setWellNetworkFilterActivated
  );

  // Hook for "Show Well To Facility Network" functionality
  const { showWellToFacilityNetwork } = useShowWellToFacilityNetwork({
    wellNetworkMapRequest,
    setWellNetworkMapRequest: setWellNetworkMapRequestAction
  });

  // Hook for "Clear Well To Facility Network" functionality
  const { clearWellToFacilityNetwork } = useClearWellToFacilityNetwork({
    mapbox
  });

  // Monitor isMidstreamNavigatorFilter to detect when filters are changed elsewhere
  const isMidstreamNavigatorFilter = useSelector(
    (state: RootState) => state.filter.isMidstreamNavigatorFilter
  );
  const isMidstreamNavigatorFacilityFilter = useSelector(
    (state: RootState) => state.facilityFilter.isMidstreamNavigatorFilter
  );

  // Monitor the highlight state from Zustand map store
  const highlightContributingWellsForFacility = useMapStore(
    selectHighlightContributingWellsForFacility
  );

  // Monitor network visualization states from Zustand map store
  const showNetworkOptions = useMapStore(selectShowNetworkOptions);
  const networkDepthLegend = useMapStore(selectNetworkDepthLegend);
  const isInActiveFilter = (filterKeyToCheck: FilterKey): boolean =>
    state.activeFilters?.some((key) => key === filterKeyToCheck) ?? false;

  // HighlightWells is independent and doesn't disable others
  const isHighlightButton = filterKey === FilterKey.HighlightWells;

  const isActive = isHighlightButton
    ? state.highlightActive
    : isInActiveFilter(filterKey);

  const hasSelectedWells = selectedWells && Object.keys(selectedWells).length > 0;
  const hasSelectedFacilities =
    selectedFacilities && Object.keys(selectedFacilities).length > 0;

  const isFacilityFilterButton = (FACILITY_BUTTONS as readonly FilterKey[]).includes(
    filterKey
  );
  const isWellFilterButton = (WELL_BUTTONS as readonly FilterKey[]).includes(filterKey);

  // Monitor for external filter changes by checking if isMidstreamNavigatorFilter was cleared
  useEffect(() => {
    // Don't auto-remove if user is actively interacting with toolbar
    if (userActionInProgress.current) {
      return;
    }

    // Only monitor facility-related filters for facility buttons
    // Well buttons shouldn't be affected by facility filter state changes
    if (isWellFilterButton) {
      return;
    }

    if (
      isActive &&
      !isHighlightButton &&
      (!isMidstreamNavigatorFilter || !isMidstreamNavigatorFacilityFilter)
    ) {
      // Only remove if this was truly an external change, not a user action
      dispatch(midstreamNavigatorActions.removeFilter(filterKey));
    }
  }, [
    isMidstreamNavigatorFilter,
    isMidstreamNavigatorFacilityFilter,
    isActive,
    isHighlightButton,
    isWellFilterButton,
    filterKey,
    dispatch
  ]);

  // Monitor Redux highlight state changes to sync with local highlight state
  useEffect(() => {
    if (
      isHighlightButton &&
      highlightContributingWellsForFacility === null &&
      state.highlightActive
    ) {
      // Redux state was cleared, deactivate local highlight
      dispatch(midstreamNavigatorActions.toggleHighlight(false));
    }
  }, [
    highlightContributingWellsForFacility,
    isHighlightButton,
    state.highlightActive,
    dispatch
  ]);

  // Track when wells are selected to update last selected entity type
  useEffect(() => {
    if (selectedWells && Object.keys(selectedWells).length > 0) {
      setLastSelectedEntityType(EntityKind.Well);
    }
  }, [selectedWells]);

  // Track when facilities are selected to update last selected entity type
  useEffect(() => {
    if (selectedFacilities && Object.keys(selectedFacilities).length > 0) {
      setLastSelectedEntityType(EntityKind.Facility);
    }
  }, [selectedFacilities]);

  // Auto-refresh network when selected wells change while WellNetwork is active
  useEffect(() => {
    if (
      filterKey === FilterKey.WellNetwork &&
      isInActiveFilter(FilterKey.WellNetwork) &&
      selectedWells &&
      Object.keys(selectedWells).length > 0
    ) {
      // Automatically refresh the network visualization when wells change
      const currentWellIds = Object.keys(selectedWells);

      const currentRequestWellIds = wellNetworkMapRequest.wellIds || [];
      const hasWellsChanged =
        currentWellIds.length !== currentRequestWellIds.length ||
        !currentWellIds.every((id) => currentRequestWellIds.includes(id));

      if (hasWellsChanged) {
        setWellNetworkMapRequestAction({
          ...wellNetworkMapRequest,
          wellIds: currentWellIds
        });

        const wellId = currentWellIds[0];
        showWellToFacilityNetwork(wellId);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedWells, filterKey, state.activeFilters, wellNetworkMapRequest]);

  // Track if network visualization was ever successfully shown
  const networkEverShown = useRef(false);

  // Add cleanup for user action flag
  useEffect(() => {
    if (userActionInProgress.current) {
      const timer = setTimeout(() => {
        userActionInProgress.current = false;
      }, 100); // Small delay to allow state changes to propagate

      return () => clearTimeout(timer);
    }
  }, [userActionInProgress.current]);

  // Track when network visualization is shown
  useEffect(() => {
    if (showNetworkOptions && networkDepthLegend.visible) {
      networkEverShown.current = true;
    }
  }, [showNetworkOptions, networkDepthLegend.visible]);

  // Monitor network visualization state changes - deactivate WellNetwork filter when network options/legend are closed
  useEffect(() => {
    // Don't auto-remove if user manually deactivated the network
    if (networkUserDeactivated.current) {
      networkUserDeactivated.current = false; // Reset flag
      return;
    }

    // Don't auto-remove if user is actively interacting
    if (userActionInProgress.current) {
      return;
    }

    if (
      filterKey === FilterKey.WellNetwork &&
      isInActiveFilter(FilterKey.WellNetwork) &&
      networkEverShown.current && // Only trigger if network was ever successfully shown
      showNetworkOptions === false && // Only trigger when showNetworkOptions was true and then became false
      networkDepthLegend.visible === false // Only trigger when networkDepthLegend was visible and then became false
    ) {
      // Network options or depth legend was closed, deactivate the WellNetwork filter
      dispatch(midstreamNavigatorActions.removeFilter(filterKey));
      networkEverShown.current = false; // Reset for next time
    }
  }, [
    filterKey,
    state.activeFilters,
    showNetworkOptions,
    networkDepthLegend.visible,
    dispatch
  ]);

  const hasNoSelectedEntity = (() => {
    if (isFacilityFilterButton) {
      return !hasSelectedFacilities;
    }

    if (isWellFilterButton) {
      return !hasSelectedWells;
    }

    return false;
  })();

  const isWrongEntityType = (() => {
    if (lastSelectedEntityType === EntityKind.Well && isFacilityFilterButton) {
      return true;
    }
    if (lastSelectedEntityType === EntityKind.Facility && isWellFilterButton) {
      return true;
    }
    return false;
  })();

  // Disable this button if no entity selected, wrong entity type selected, or another filter is active (but not if it's highlight or this button is active)
  const isDisabled = (() => {
    // Basic validation - always disable if wrong entity or no entity
    if (hasNoSelectedEntity || isWrongEntityType) {
      return true;
    }

    // For facility buttons, disable if no facilities selected
    if (isFacilityFilterButton && !hasSelectedFacilities) {
      return true;
    }

    // For well buttons, allow multiple to be active (less restrictive)
    if (isWellFilterButton) {
      return false; // Well buttons can be active simultaneously
    }

    // For facility buttons, allow if this button is active OR no other facility filters are active
    if (isFacilityFilterButton) {
      if (filterKey === FilterKey.HighlightWells) {
        return false; // Highlight can be active with others
      }
      const otherActiveFacilityFilters = FACILITY_BUTTONS.filter(
        (button) =>
          button !== filterKey &&
          button !== FilterKey.HighlightWells &&
          isInActiveFilter(button)
      );
      return otherActiveFacilityFilters.length > 0 && !isActive;
    }

    return false;
  })();

  const handleToggle = (shouldActivate: boolean) => {
    // Set flag to indicate user is actively interacting
    userActionInProgress.current = true;
    lastUserAction.current = shouldActivate ? "activate" : "deactivate";

    if (shouldActivate) {
      if (isFacilityFilterButton) {
        // Clear any existing facility filters (except highlight)
        const existingFacilityFilters = FACILITY_BUTTONS.filter(
          (button) => button !== FilterKey.HighlightWells && isInActiveFilter(button)
        );

        existingFacilityFilters.forEach((existingFilter) => {
          dispatch(midstreamNavigatorActions.removeFilter(existingFilter));
        });

        // Clear filters before activating new one
        if (existingFacilityFilters.length > 0) {
          undoFilters();
        }
      }
      switch (filterKey) {
        case FilterKey.HighlightWells:
          if (hasSelectedFacilities) {
            dispatch(midstreamNavigatorActions.toggleHighlight(true));
            reduxDispatch(setIsMidstreamNavigatorFilter(true));
            reduxDispatch(setIsMidstreamNavigatorFacilityFilter(true));
            const facilityId = Object.keys(selectedFacilities)[0];
            setHighlightContributingWellsForFacility(facilityId);
          }
          break;
        case FilterKey.FacilityAndWells:
          dispatch(midstreamNavigatorActions.addFilter(filterKey));
          filterToFacilityAndContributingWells();
          break;
        case FilterKey.ContributingWellsFromAllFacilities:
          dispatch(midstreamNavigatorActions.addFilter(filterKey));
          filterToContributingWellsFromAllFacilities();
          break;
        case FilterKey.SalesFacilities:
          dispatch(midstreamNavigatorActions.addFilter(filterKey));
          filterToSalesFacilities();
          break;
        case FilterKey.WellNetwork:
          if (hasSelectedWells) {
            dispatch(midstreamNavigatorActions.addFilter(filterKey));
            setWellNetworkFilterActivated(true);
            const wellId = Object.keys(selectedWells)[0];
            showWellToFacilityNetwork(wellId);
          }
          break;
        default:
          break;
      }
    } else {
      if (isHighlightButton) {
        dispatch(midstreamNavigatorActions.toggleHighlight(false));
        clearHighlightContributingWellsForFacility();
      } else if (filterKey === FilterKey.WellNetwork) {
        // Set flag to indicate user manually deactivated network
        networkUserDeactivated.current = true;

        dispatch(midstreamNavigatorActions.removeFilter(filterKey));
        setWellNetworkFilterActivated(false);
        clearWellToFacilityNetwork();
        clearEnvelopes();
      } else {
        dispatch(midstreamNavigatorActions.removeFilter(filterKey));
        clearEnvelopes();
        undoFilters();
      }
    }

    // Reset user action flag after a short delay to allow state propagation
    setTimeout(() => {
      userActionInProgress.current = false;
      lastUserAction.current = null;
    }, 150);
  };
  const facilityFilters = {
    [FilterKey.HighlightWells]: {
      icon: <Icon_MidstreamHighlight />,
      tooltip: "Highlight Contributing Wells for Selected Facility",
      onClick: () => handleToggle(!isActive)
    },
    [FilterKey.FacilityAndWells]: {
      icon: <Icon_MidstreamContributingWells />,
      tooltip: "Filter To Facility and Contributing Wells",
      onClick: () => handleToggle(!isActive)
    },
    [FilterKey.ContributingWellsFromAllFacilities]: {
      icon: <Icon_MidstreamAllFacilities />,
      tooltip: "Filter To Contributing Wells From All Facilities",
      onClick: () => handleToggle(!isActive)
    },
    [FilterKey.Network]: {
      icon: <Icon_MidstreamNetwork />,
      tooltip: "Filter To Network",
      onClick: () => handleToggle(!isActive)
    }
  };

  const wellFilters = {
    [FilterKey.SalesFacilities]: {
      icon: <Icon_MidstreamSalesFacilities />,
      tooltip: "Filter to Sales Facilities for Selected Well",
      onClick: () => handleToggle(!isActive)
    },
    [FilterKey.WellNetwork]: {
      icon: <Icon_MidstreamNetwork />,
      tooltip: "Show Well to Facility Network for Selected Well",
      onClick: () => handleToggle(!isActive)
    }
  };

  // Combine all filters so all buttons show regardless of mode
  const allFilters = { ...facilityFilters, ...wellFilters };
  const filter = allFilters[filterKey];

  if (!filter) return null;
  return (
    <ToolbarButton
      active={isActive}
      disabled={isDisabled}
      icon={filter.icon}
      onToggle={handleToggle}
      tooltipText={filter.tooltip}
      testId={`filter-button-${filterKey}`}
      styleKey="mapToolbar"
    />
  );
};

export default FilterButton;
